import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuthenticatedUser } from '@sparkai/auth'

import { apiClient } from '../../apiClient'
import { useGetMineScopes } from '../../user/useProfile'

export interface Organization {
  id: string
  logoUrl?: string
  name: string
}

type OrganizationResponse = Omit<Organization, 'logoUrl'> & {
  logoUrl?: string | null
}

type OrganizationsResponse = {
  total: number
  organizations: OrganizationResponse[]
}

const normalizeOrganization = (organization: OrganizationResponse): Organization => ({
  ...organization,
  logoUrl: organization.logoUrl ?? undefined,
})

const ORGANIZATIONS_PAGE_LIMIT = 100

type OrganizationsListParams = {
  orderBy: 'createdAt' | 'name'
  sortDescending: boolean
  status: 'active'
  onlyActivePrograms?: boolean
}

const fetchAllOrganizations = async (params: OrganizationsListParams): Promise<OrganizationResponse[]> => {
  const organizations: OrganizationResponse[] = []
  let cursor = 0
  let total = Infinity

  while (cursor < total) {
    const { data } = await apiClient.get<OrganizationsResponse>('/int/v3/organizations', {
      params: { ...params, cursor, limit: ORGANIZATIONS_PAGE_LIMIT },
    })

    organizations.push(...data.organizations)
    total = data.total
    cursor += ORGANIZATIONS_PAGE_LIMIT
  }

  return organizations
}

export const ORG_PROFILE_LIST_KEY = 'org-profile-list'
export const ORG_PROFILE_LIST_ACTIVE_PROGRAMS_KEY = 'org-profile-list-active-programs'
export const ORG_PROFILE_MINE_KEY = 'org-profile-mine'

export function useGetList() {
  return useQuery({
    queryKey: [ORG_PROFILE_LIST_KEY],
    queryFn: async () => {
      const organizations = await fetchAllOrganizations({
        orderBy: 'createdAt',
        sortDescending: false,
        status: 'active',
      })

      return organizations.map(normalizeOrganization)
    },
    refetchOnWindowFocus: false,
  })
}

export function useCreate() {
  const queryClient = useQueryClient()

  const { mutateAsync } = useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post<{ id: string }>('/int/v3/organizations', { name })

      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ORG_PROFILE_LIST_KEY] }),
  })

  return { createOrganization: mutateAsync }
}

export function useGetListWithActivePrograms() {
  return useQuery({
    queryKey: [ORG_PROFILE_LIST_ACTIVE_PROGRAMS_KEY, true],
    queryFn: async () => {
      const organizations = await fetchAllOrganizations({
        orderBy: 'name',
        sortDescending: false,
        status: 'active',
        onlyActivePrograms: true,
      })

      return organizations.map(normalizeOrganization)
    },
  })
}

export function useGetMine() {
  const { profile } = useAuthenticatedUser()
  const scopesQuery = useGetMineScopes()
  // `profile.org` only carries the organization name (no stable id), so the current scope is
  // resolved by name against the user's own scope list. This relies on `profile` being kept in
  // sync with the active scope (see useChangeUserScope, which refreshes the token and invalidates
  // queries on scope change) and on a user never holding two scopes with the same organization name.
  const orgId = scopesQuery.data?.find((scope) => scope.organization.name === profile?.org)?.organization.id
  const organizationQuery = useQuery<Organization>({
    queryKey: [ORG_PROFILE_MINE_KEY, orgId],
    queryFn: async () => {
      const { data } = await apiClient.get<OrganizationResponse>(`/int/v3/organizations/${orgId}`)
      return normalizeOrganization(data)
    },
    enabled: Boolean(orgId),
  })

  return {
    ...organizationQuery,
    isLoading: scopesQuery.isLoading || organizationQuery.isLoading,
    isError: scopesQuery.isError || organizationQuery.isError,
    error: scopesQuery.error ?? organizationQuery.error,
  }
}
